import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

/**
 * BristleBrush - A theme-aware brush visualization component
 * 
 * @param {Object} props - Component props
 * @param {number} [props.opacity] - Opacity of the brush stroke (0-1)
 * @param {number} [props.width] - Width of the SVG
 * @param {number} [props.height] - Height of the SVG
 * @param {string} [props.stroke] - Stroke color
 * @param {number} [props.strokeWidth] - Stroke width
 * @param {string} [props.path] - SVG path data (d attribute)
 * @param {boolean} [props.useSoftEdge] - Enable/disable soft edge filter
 * @param {boolean} [props.useBristle] - Enable/disable bristle filter
 * @param {Object} [props.bristleConfig] - Configuration for bristle filter
 * @param {Object} [props.gradientColors] - Start and end colors for gradient
 * 
 * @returns {React.Component} Themed bristle brush
 */
const BristleBrush = ({ 
  opacity,
  width,
  height,
  stroke,
  strokeWidth,
  path,
  useSoftEdge,
  useBristle,
  bristleConfig,
  gradientColors,
  ...restProps 
}) => {
  const theme = useTheme();
  const isThemedSkin = theme && theme.name !== 'blank';
  
  // Generate unique IDs for filters and gradients
  const softEdgeId = `softEdgeEffect-${uuidv4().slice(0, 8)}`;
  const bristleId = `bristleEffect-${uuidv4().slice(0, 8)}`;
  const gradientId = `gradient-${uuidv4().slice(0, 8)}`;
  
  // Get theme properties or use defaults
  const themeOpacity = isThemedSkin ? theme.bristleBrush.defaultOpacity : 1;
  const themeWidth = isThemedSkin ? theme.bristleBrush.width : 400;
  const themeHeight = isThemedSkin ? theme.bristleBrush.height : 400;
  const themeStroke = isThemedSkin ? theme.bristleBrush.stroke : '#456837';
  const themeStrokeWidth = isThemedSkin ? theme.bristleBrush.strokeWidth : 30;
  const themePath = isThemedSkin ? theme.bristleBrush.path.d : 'M 50 150 Q 150 50 250 150 T 450 150';
  
  // Get filter configurations
  const themeSoftEdgeEnabled = isThemedSkin && theme.bristleBrush.filters.softEdge.enabled;
  const themeBristleEnabled = isThemedSkin && theme.bristleBrush.filters.bristle.enabled;
  
  // Use props if provided, otherwise use theme values
  const brushOpacity = opacity !== undefined ? opacity : themeOpacity;
  const brushWidth = width || themeWidth;
  const brushHeight = height || themeHeight;
  const brushStroke = stroke || themeStroke;
  const brushStrokeWidth = strokeWidth || themeStrokeWidth;
  const brushPath = path || themePath;
  
  // Use props if provided, otherwise use theme values for filters
  const enableSoftEdge = useSoftEdge !== undefined ? useSoftEdge : themeSoftEdgeEnabled;
  const enableBristle = useBristle !== undefined ? useBristle : themeBristleEnabled;
  
  // Get gradient colors
  const startColor = gradientColors?.start || 
    (isThemedSkin ? theme.bristleBrush.gradient.startColor : '#000000');
  const endColor = gradientColors?.end || 
    (isThemedSkin ? theme.bristleBrush.gradient.endColor : '#FFFFFF');
  
  // Get bristle filter config
  const baseFrequency = bristleConfig?.baseFrequency || 
    (isThemedSkin ? theme.bristleBrush.filters.bristle.baseFrequency : 0.5);
  const numOctaves = bristleConfig?.numOctaves || 
    (isThemedSkin ? theme.bristleBrush.filters.bristle.numOctaves : 2);
  const scale = bristleConfig?.scale || 
    (isThemedSkin ? theme.bristleBrush.filters.bristle.scale : 10);
  
  // Get soft edge filter config
  const blurAmount = isThemedSkin ? theme.bristleBrush.filters.softEdge.blur : 3;

  return (
    <>
      <svg width={brushWidth} height={brushHeight} xmlns="http://www.w3.org/2000/svg" {...restProps}>
        <defs>
          {enableSoftEdge && (
            <filter id={softEdgeId}>
              <feGaussianBlur stdDeviation={blurAmount}/>
            </filter>
          )}
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={startColor}/>
            <stop offset="100%" stopColor={endColor}/>
          </linearGradient>
        </defs>
        
        {/* Soft edge effect (if enabled) */}
        {enableSoftEdge && (
          <path 
            d={brushPath} 
            stroke={`url(#${gradientId})`} 
            strokeWidth={brushStrokeWidth} 
            fill="none"
            filter={`url(#${softEdgeId})`}
            style={{ opacity: brushOpacity }}
          />
        )}
        
        {/* Base stroke */}
        <path 
          d={brushPath} 
          stroke={`url(#${gradientId})`} 
          strokeWidth={brushStrokeWidth / 2} 
          fill="none"
          style={{ opacity: brushOpacity }}
        />
      </svg>
      
      {/* Second SVG for bristle effect (if enabled) */}
      {enableBristle && (
        <svg width={brushWidth} height={brushHeight} xmlns="http://www.w3.org/2000/svg" {...restProps}>
          <defs>
            <filter id={bristleId}>
              <feTurbulence
                type="fractalNoise"
                baseFrequency={baseFrequency}
                numOctaves={numOctaves}
                result="noise"
              />
              <feDisplacementMap in="SourceGraphic" in2="noise" scale={scale}/>
            </filter>
          </defs>
          
          <g filter={`url(#${bristleId})`}>
            <path
              d={brushPath}
              stroke={brushStroke}
              strokeWidth={brushStrokeWidth}
              fill="none"
              style={{ opacity: brushOpacity }}
            />
          </g>
        </svg>
      )}
    </>
  );
};

BristleBrush.propTypes = {
  /** Opacity of the brush stroke (0-1) */
  opacity: PropTypes.number,
  /** Width of the SVG */
  width: PropTypes.number,
  /** Height of the SVG */
  height: PropTypes.number,
  /** Stroke color */
  stroke: PropTypes.string,
  /** Stroke width */
  strokeWidth: PropTypes.number,
  /** SVG path data (d attribute) */
  path: PropTypes.string,
  /** Enable/disable soft edge filter */
  useSoftEdge: PropTypes.bool,
  /** Enable/disable bristle filter */
  useBristle: PropTypes.bool,
  /** Configuration for bristle filter */
  bristleConfig: PropTypes.shape({
    baseFrequency: PropTypes.number,
    numOctaves: PropTypes.number,
    scale: PropTypes.number
  }),
  /** Start and end colors for gradient */
  gradientColors: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string
  })
};

export default BristleBrush;

